import { fetchAuthSession } from 'aws-amplify/auth';
import { domainRouterClient } from '../api/domainRouterClient';
import {
  clearPersonaSignupIntentForEmail,
  readPersonaSignupIntent,
  readPersonaSignupIntentForEmail,
} from './personaProvisioningStorage';

export type PersonaProvisioningResult = {
  status: 'assigned' | 'already_assigned' | 'pending_staff_approval' | 'no_change';
  persona: string;
  assignedGroup: string | null;
  pendingApproval: boolean;
  reason: string;
  reauthenticationRequired?: boolean;
  observedPayloadType?: string | null;
  observedPayloadKeys?: string[];
};

export type PersonaTokenClaimResult = {
  targetPersona: string;
  ready: boolean;
  groups: string[];
  attempts: number;
  reason: string;
};

export type ExplicitPersonaProvisioningInput = {
  requestedPersona?: 'parent' | 'staff' | 'administrator' | null;
  requestedAccountType?: 'parent' | 'staff' | null;
  schoolId?: string | null;
  staffSignupCode?: string | null;
  intentEmail?: string | null;
  financialAidInvite?: boolean | null;
};

const toAwsJson = (value: unknown): string => JSON.stringify(value);

const normalizeText = (value: unknown): string => String(value ?? '').trim();

const normalizeGroupToken = (value: string): string =>
  normalizeText(value).toLowerCase();

const PERSONA_GROUP_EQUIVALENTS: Record<string, string[]> = {
  parent: ['parent'],
  staff: ['staff', 'editor', 'nurse'],
  viewer: ['viewer'],
  administrator: [
    'administrator',
    'admin',
    'owner',
    'super-administrator',
  ],
  admin: [
    'administrator',
    'admin',
    'owner',
    'super-administrator',
  ],
  owner: ['owner', 'admin', 'administrator'],
  'super-administrator': [
    'super-administrator',
    'owner',
    'admin',
    'administrator',
  ],
};

const resolveSessionGroups = (session: unknown): string[] => {
  if (!session || typeof session !== 'object') {
    return [];
  }
  const sessionRecord = session as Record<string, unknown>;
  const tokens = sessionRecord.tokens && typeof sessionRecord.tokens === 'object'
    ? (sessionRecord.tokens as Record<string, unknown>)
    : null;
  const idToken = tokens?.idToken && typeof tokens.idToken === 'object'
    ? (tokens.idToken as Record<string, unknown>)
    : null;
  const payload = idToken?.payload && typeof idToken.payload === 'object'
    ? (idToken.payload as Record<string, unknown>)
    : null;
  if (!payload) {
    return [];
  }
  const rawGroups = payload['cognito:groups'];
  if (Array.isArray(rawGroups)) {
    return rawGroups.map((entry) => normalizeText(entry)).filter(Boolean);
  }
  if (typeof rawGroups === 'string') {
    return rawGroups.split(',').map((entry) => normalizeText(entry)).filter(Boolean);
  }
  return [];
};

const hasTargetPersonaGroup = (groups: string[], targetPersona: string): boolean => {
  const normalizedTarget = normalizeGroupToken(targetPersona);
  const allowedGroups = PERSONA_GROUP_EQUIVALENTS[normalizedTarget] ?? [normalizedTarget];
  const allowed = new Set(allowedGroups.map((entry) => normalizeGroupToken(entry)));
  return groups.some((group) => allowed.has(normalizeGroupToken(group)));
};

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const parseJsonCandidate = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  const parsed = parseJsonCandidate(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }
  return parsed as Record<string, unknown>;
};

const describeProvisionPayloadShape = (
  payload: unknown,
): {
  observedPayloadType: string;
  observedPayloadKeys: string[];
} => {
  const parsed = parseJsonCandidate(payload);
  if (Array.isArray(parsed)) {
    return {
      observedPayloadType: 'array',
      observedPayloadKeys: [],
    };
  }
  if (parsed && typeof parsed === 'object') {
    return {
      observedPayloadType: 'object',
      observedPayloadKeys: Object.keys(parsed as Record<string, unknown>).sort(),
    };
  }
  return {
    observedPayloadType: typeof parsed,
    observedPayloadKeys: [],
  };
};

const extractProvisionResult = (payload: unknown): PersonaProvisioningResult | null => {
  const root = asRecord(payload);
  if (!root) {
    return null;
  }
  const result = asRecord(root.provisionSelfPersona ?? root);
  if (!result) {
    return null;
  }
  const status = normalizeText(result.status).toLowerCase();
  const persona = normalizeText(result.persona);
  const reason = normalizeText(result.reason);
  if (!status || !persona || !reason) {
    return null;
  }

  if (
    status !== 'assigned'
    && status !== 'already_assigned'
    && status !== 'pending_staff_approval'
    && status !== 'no_change'
  ) {
    return null;
  }

  const assignedGroup = normalizeText(result.assignedGroup);
  return {
    status,
    persona,
    assignedGroup: assignedGroup || null,
    pendingApproval: Boolean(result.pendingApproval),
    reason,
    reauthenticationRequired: Boolean(result.reauthenticationRequired),
    observedPayloadType: 'object',
    observedPayloadKeys: Object.keys(result).sort(),
  };
};

const needsTokenRefresh = (status: PersonaProvisioningResult['status']): boolean =>
  status === 'assigned' || status === 'already_assigned';

const TRUSTED_STAFF_SIGNUP_ASSIGNMENT_REASONS = new Set([
  'staff_signup_auto_approved',
  'staff_invitation_auto_approved',
  'staff_directory_role_auto_approved',
]);

const isStaffLaneAssignment = (result: PersonaProvisioningResult): boolean => {
  const persona = normalizeGroupToken(result.assignedGroup || result.persona);
  return persona === 'staff' || persona === 'editor' || persona === 'nurse' || persona === 'viewer';
};

const isUnsafeStaffSignupAssignment = ({
  result,
  requestedAccountType,
  requestedPersona,
}: {
  result: PersonaProvisioningResult;
  requestedAccountType: ExplicitPersonaProvisioningInput['requestedAccountType'];
  requestedPersona: ExplicitPersonaProvisioningInput['requestedPersona'];
}): boolean => {
  const requestedStaff =
    requestedAccountType === 'staff'
    || requestedPersona === 'staff';
  if (
    !requestedStaff
    || (result.status !== 'assigned' && result.status !== 'already_assigned')
    || !isStaffLaneAssignment(result)
  ) {
    return false;
  }
  return !TRUSTED_STAFF_SIGNUP_ASSIGNMENT_REASONS.has(result.reason);
};

const coerceUnsafeStaffSignupResult = ({
  result,
  requestedAccountType,
  requestedPersona,
}: {
  result: PersonaProvisioningResult;
  requestedAccountType: ExplicitPersonaProvisioningInput['requestedAccountType'];
  requestedPersona: ExplicitPersonaProvisioningInput['requestedPersona'];
}): PersonaProvisioningResult => {
  if (!isUnsafeStaffSignupAssignment({
    result,
    requestedAccountType,
    requestedPersona,
  })) {
    return result;
  }

  return {
    ...result,
    status: 'pending_staff_approval',
    persona: 'guest',
    assignedGroup: null,
    pendingApproval: true,
    reason: 'staff_signup_pending_approval',
  };
};

export const ensurePersonaTokenClaim = async (
  targetPersona: string,
  options?: {
    maxAttempts?: number;
    baseDelayMs?: number;
  }
): Promise<PersonaTokenClaimResult> => {
  const target = normalizeGroupToken(targetPersona);
  if (!target) {
    return {
      targetPersona: targetPersona,
      ready: false,
      groups: [],
      attempts: 0,
      reason: 'missing target persona',
    };
  }

  const maxAttempts = Math.max(1, options?.maxAttempts ?? 6);
  const baseDelayMs = Math.max(50, options?.baseDelayMs ?? 250);
  let lastGroups: string[] = [];
  let lastError: unknown = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const groups = resolveSessionGroups(session);
      lastGroups = groups;
      if (hasTargetPersonaGroup(groups, target)) {
        return {
          targetPersona: target,
          ready: true,
          groups,
          attempts: attempt,
          reason: 'claim-present',
        };
      }
      if (attempt < maxAttempts) {
        await wait(baseDelayMs * attempt);
      }
    } catch (error) {
      lastError = error;
      if (attempt < maxAttempts) {
        await wait(baseDelayMs * attempt);
      }
    }
  }

  return {
    targetPersona: target,
    ready: false,
    groups: lastGroups,
    attempts: maxAttempts,
    reason: lastError instanceof Error
      ? `session-refresh-failed: ${lastError.message}`
      : 'claim-missing-after-refresh',
  };
};

export const mapProvisionedPersonaToShellPersona = (persona: string): 'parent' | 'staff' | 'administrator' | 'super-administrator' | 'guest' => {
  const normalized = normalizeText(persona).toLowerCase();
  if (normalized === 'parent') {
    return 'parent';
  }
  if (normalized === 'staff' || normalized === 'editor' || normalized === 'nurse') {
    return 'staff';
  }
  if (normalized === 'super-administrator') {
    return 'super-administrator';
  }
  if (normalized === 'administrator' || normalized === 'admin' || normalized === 'owner') {
    return 'administrator';
  }
  return 'guest';
};

export const provisionActiveSessionPersona = async (options?: {
  emailHint?: string | null;
  requestedPersona?: ExplicitPersonaProvisioningInput['requestedPersona'];
  requestedAccountType?: ExplicitPersonaProvisioningInput['requestedAccountType'];
  schoolId?: string | null;
  staffSignupCode?: string | null;
  intentEmail?: string | null;
  financialAidInvite?: boolean | null;
  source?: string;
}): Promise<PersonaProvisioningResult | null> => {
  const emailHint = normalizeText(options?.emailHint).toLowerCase();
  const intent = emailHint
    ? readPersonaSignupIntentForEmail(emailHint)
    : readPersonaSignupIntent();
  const requestedPersona = options?.requestedPersona ?? intent?.requestedPersona ?? null;
  const requestedAccountType = options?.requestedAccountType ?? intent?.requestedAccountType ?? (
    requestedPersona === 'parent' || requestedPersona === 'staff' ? requestedPersona : null
  );
  const staffSignupCode = options?.staffSignupCode ?? intent?.staffSignupCode ?? null;
  const schoolId = options?.schoolId ?? intent?.schoolId ?? null;
  const intentEmail = normalizeText(options?.intentEmail ?? intent?.email ?? '').toLowerCase();
  const financialAidInvite = options?.financialAidInvite ?? intent?.financialAidInvite ?? false;

  const inputPayload: Record<string, unknown> = {
    source: normalizeText(options?.source) || 'session-refresh',
  };

  if (requestedAccountType) {
    inputPayload.requestedAccountType = requestedAccountType;
  }
  if (requestedPersona) {
    inputPayload.requestedPersona = requestedPersona;
  }
  if (staffSignupCode) {
    inputPayload.staffSignupCode = staffSignupCode;
  }
  if (schoolId) {
    inputPayload.schoolId = schoolId;
  }
  if (intentEmail && financialAidInvite) {
    inputPayload.intentEmail = intentEmail;
  }
  if (financialAidInvite) {
    inputPayload.financialAidInvite = true;
  }

  const data = await domainRouterClient.mutate('provisionSelfPersona', {
    input: toAwsJson(inputPayload),
  });
  const parsedResult = extractProvisionResult(data);
  const rawResult = parsedResult ?? {
    status: 'no_change' as const,
    persona: 'guest',
    assignedGroup: null,
    pendingApproval: false,
    reason: 'persona-provisioning-no-result',
    ...describeProvisionPayloadShape(data),
  };
  const result = coerceUnsafeStaffSignupResult({
    result: rawResult,
    requestedAccountType,
    requestedPersona,
  });

  if (!parsedResult) {
    console.warn('[auth] provisionSelfPersona returned an unusable payload shape', {
      observedPayloadType: result.observedPayloadType,
      observedPayloadKeys: result.observedPayloadKeys,
    });
  }

  if (parsedResult && emailHint && result.status !== 'pending_staff_approval') {
    clearPersonaSignupIntentForEmail(emailHint);
  }

  if (needsTokenRefresh(result.status) && !result.reauthenticationRequired) {
    const targetPersona = result.assignedGroup || result.persona;
    const claim = await ensurePersonaTokenClaim(targetPersona, {
      maxAttempts: 4,
      baseDelayMs: 200,
    });
    console.info('[auth] persona_token_refresh_attempt', {
      targetPersona: claim.targetPersona,
      ready: claim.ready,
      attempts: claim.attempts,
      groups: claim.groups,
      reason: claim.reason,
    });
  }

  return result;
};
